import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Footer from "../components/Footer";
import { PitchVideo } from "../components/PitchVideo";
import { BillingCycleToggle, annualAmount } from "../components/BillingCycleToggle";
import { SubscribeLeadForm } from "../components/SubscribeLeadForm";
import { formatSgd } from "../lib/utils";
import {
  ShieldCheck, ArrowRight, Check, Sparkles, FileCheck2, Boxes,
  Gauge, Building2, BadgeCheck, Landmark, ThermometerSun, Store,
  Sprout, BrainCircuit, Radar, Scale, Wallet, Network, LineChart, Layers, Bot, Users,
} from "lucide-react";

const INK = "#0F3D3A";
const TEAL = "#117A7A";
const GOLD = "#D4A84C";

const POSITIONING = "Your all-in-one sustainability and ESG platform, designed for Asian companies.";

const PILLARS = [
  { icon: Sprout, label: "Sustainable Growth" },
  { icon: BrainCircuit, label: "AI & Data Intelligence" },
  { icon: Radar, label: "Control Tower Visibility" },
  { icon: ShieldCheck, label: "Governance & Resilience" },
  { icon: Scale, label: "Balance & Harmony" },
];

const MODULES = [
  { icon: Gauge, title: "Control Tower", tag: "Measure", body: "A board-ready cockpit: live Scope 1/2/3 emissions, materiality, scorecards, custom KPIs, facilities and a net-zero roadmap that moves as your data does — switchable across financial years." },
  { icon: FileCheck2, title: "Reporting & Compliance", tag: "Comply", body: "IFRS S1/S2, SGX Core Metrics, GRI and TCFD mapped line by line — locked baselines, evidence trails, approval workflows, and one-click PDF, iXBRL and ESEF exports your auditors will accept." },
  { icon: ThermometerSun, title: "Scenarios & Climate Risk", tag: "De-risk", body: "NGFS climate scenarios, a quantified risk register with S$ financial exposure, heatmaps — and adaptation plans for heat, water and flood risk linked to funded initiatives." },
  { icon: Boxes, title: "Supply Chain Control Tower", tag: "Engage", body: "Map key suppliers, run data-request campaigns, and govern a Scope 3 Data Bridge with product-level traceability, a disclosure lifecycle and explainable supplier scoring — so supplier facts reach your inventory only when a human approves them." },
  { icon: Network, title: "Scope 3 Intelligence & Exchange", tag: "Decarbonise", premium: true, body: "A premium module: an AI Scope 3 mapper (advisory), the Supplier Decarbonisation Opportunity Engine, PACT V3 machine-to-machine PCF exchange, and time-series supplier carbon, data-quality and engagement KPI trends." },
  { icon: Landmark, title: "Green Financing & Offset", tag: "Fund", body: "An investment register matching your projects to Singapore grants and bank green loans, bankable cases with NPV/IRR, sustainability-linked loan targets and the CFO's whole capital position." },
  { icon: Layers, title: "Materiality & Double Materiality", tag: "Focus", body: "Industry-anchored material topics, a double-materiality matrix, assessment and evidence workflows, approvals and audit trail — a right-sized, defensible ESG agenda." },
  { icon: LineChart, title: "Score Intelligence", tag: "Prove", body: "Turn ESG data into five decision outputs — performance, resilience, business value, evidence confidence and stakeholder readiness — coverage-normalised and fully traceable." },
  { icon: Bot, title: "AI-Powered Data Management", tag: "Automate", body: "Drop in raw utility bills and CSVs — AI maps headers, applies Singapore emission factors, flags anomalies, drafts disclosures and answers funding questions through an always-on copilot." },
];

const AUDIENCES = [
  { icon: Sprout, title: "Sustainability leaders", body: "Run measurement, materiality, disclosure and supplier engagement from one governed system of record — without a spreadsheet army." },
  { icon: Wallet, title: "Finance leaders", body: "See carbon-tax exposure, green-financing options and the whole capital position, with audit-ready evidence the CFO can defend." },
  { icon: Network, title: "Operations & supply-chain leaders", body: "Map suppliers, request ESG data, score exposure and decarbonise the value chain — before disruption or a buyer questionnaire hits." },
  { icon: Building2, title: "Boards & executives", body: "A live control tower with quantified climate risk and readiness scoring — glanceable oversight without re-keying." },
];

const STEPS = [
  { n: "01", title: "Start with your work email", body: "Verify your company email with a 6-digit code — no government login needed." },
  { n: "02", title: "Tell us about your organisation", body: "Declare your UEN, industry and goals; we tailor materiality and workflows to you." },
  { n: "03", title: "Provision in minutes", body: "An AI-guided wizard sets up owners, baseline data and your secure tenant." },
  { n: "04", title: "Report with confidence", body: "Track progress, close compliance gaps and generate investor-grade disclosures." },
];

const serif = { fontFamily: "Georgia, 'Times New Roman', serif" };

export default function Landing() {
  const [packages, setPackages] = useState([]);
  const [recCode] = useState("growth");
  const [cycle, setCycle] = useState("monthly");
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [subscribePlan, setSubscribePlan] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/onboarding/packages").then((r) => setPackages(r.data.packages || [])).catch(() => {});
  }, []);

  const go = () => navigate("/get-started");
  const openSubscribe = (plan) => { setSubscribePlan(plan); setSubscribeOpen(true); };

  return (
    <div className="min-h-screen bg-[#F2F4F3] text-[#0F3D3A]" data-testid="landing-page">
      <SubscribeLeadForm open={subscribeOpen} onOpenChange={setSubscribeOpen} defaultPlan={subscribePlan} />

      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-[#0F3D3A]/10 bg-[#F2F4F3]/90 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/welcome" className="flex items-center" data-testid="brand-home">
            <img src="/brand/logo_horizontal.png" alt="Lestari" className="h-9 object-contain" />
          </Link>
          <nav className="flex items-center gap-6">
            <Link to="/about" className="text-sm text-[#0F3D3A]/70 hover:text-[#0F3D3A] transition-colors hidden sm:block" data-testid="nav-about">About</Link>
            <a href="#pricing" className="text-sm text-[#0F3D3A]/70 hover:text-[#0F3D3A] transition-colors hidden sm:block">Pricing</a>
            <Link to="/login" className="text-sm text-[#0F3D3A]/70 hover:text-[#0F3D3A] transition-colors" data-testid="nav-signin">Sign in</Link>
            <button onClick={go} data-testid="nav-start-trial"
              className="inline-flex items-center gap-1.5 bg-[#0F3D3A] hover:bg-[#117A7A] text-[#F2F4F3] text-sm font-semibold px-4 py-2 rounded-full transition-colors">
              Start free trial <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-40 -right-40 h-[480px] w-[480px] rounded-full opacity-[0.07]" style={{ background: `radial-gradient(circle, ${TEAL}, transparent 70%)` }} />
        <div className="max-w-6xl mx-auto px-6 pt-14 pb-14 lg:pt-20 lg:pb-20 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <div className="mb-5 font-mono text-[10px] sm:text-xs uppercase tracking-[0.22em] text-[#117A7A]" data-testid="hero-eyebrow">
              THE SUSTAINABILITY &amp; ESG PLATFORM FOR ASIAN COMPANIES
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-bold tracking-tight leading-[1.05]" style={serif} data-testid="hero-brand-line">
              Intelligent guidance.<br />Sustainable growth.<br /><span style={{ color: GOLD }}>Lasting impact.</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg max-w-xl leading-relaxed text-[#0F3D3A]/75" data-testid="hero-positioning">
              <span className="font-semibold text-[#0F3D3A]">{POSITIONING}</span> Lestari measures your footprint, keeps
              you compliant, de-risks your operations, engages your supply chain, decarbonises your value chain — then
              finds the grants and green financing to fund what comes next.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button onClick={go} data-testid="hero-start-trial"
                className="inline-flex items-center gap-2 bg-[#0F3D3A] hover:bg-[#117A7A] text-[#F2F4F3] font-semibold px-6 py-3 rounded-full transition-colors shadow-lg">
                <ShieldCheck className="h-4 w-4" /> Start your 15-day trial <ArrowRight className="h-4 w-4" />
              </button>
              <a href="#pricing" className="text-sm text-[#117A7A] hover:text-[#0F3D3A] font-medium transition-colors">See pricing →</a>
            </div>
            <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-xs text-[#0F3D3A]/60 font-mono">
              <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5" style={{ color: TEAL }} /> No credit card to start</span>
              <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5" style={{ color: TEAL }} /> Start with your work email</span>
              <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5" style={{ color: TEAL }} /> Cancel anytime</span>
            </div>
          </div>
          <div>
            <PitchVideo />
            <div className="mt-3 text-center text-[11px] font-mono tracking-wide text-[#0F3D3A]/50">
              Singapore has declared 2026 the Year of Climate Adaptation. Is your business ready?
            </div>
          </div>
        </div>

        {/* Brand pillars strip */}
        <div className="border-y border-[#0F3D3A]/10 bg-white/60">
          <div className="max-w-6xl mx-auto px-6 py-5 flex flex-wrap items-center justify-center gap-x-10 gap-y-3" data-testid="brand-pillars">
            {PILLARS.map((p, i) => (
              <React.Fragment key={p.label}>
                {i > 0 && <span className="hidden md:block h-1 w-1 rounded-full" style={{ background: GOLD }} />}
                <span className="flex items-center gap-2 text-xs sm:text-sm font-medium text-[#0F3D3A]/80">
                  <p.icon className="h-4 w-4" style={{ color: TEAL }} /> {p.label}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#117A7A] mb-3">The Lestari AI Platform</div>
          <h2 className="text-3xl font-bold tracking-tight max-w-2xl" style={serif}>One platform. The full sustainability lifecycle.</h2>
          <p className="text-[#0F3D3A]/65 text-sm mt-3 max-w-2xl leading-relaxed">
            Measure → comply → de-risk → engage the supply chain → decarbonise → fund → grow. Every stage lives in one
            governed system of record — {POSITIONING.toLowerCase()}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {MODULES.map((f) => (
              <div key={f.title} className="rounded-xl border border-[#0F3D3A]/10 bg-[#F2F4F3] p-5 hover:shadow-md transition-shadow relative" data-testid={`module-card-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {f.premium && <span className="absolute top-4 right-4 text-[9px] font-bold tracking-wide px-2 py-0.5 rounded-full" style={{ background: GOLD, color: INK }}>PREMIUM</span>}
                <div className="h-10 w-10 rounded-lg flex items-center justify-center mb-4" style={{ background: "#117A7A14", border: "1px solid #117A7A33" }}>
                  <f.icon className="h-5 w-5" style={{ color: TEAL }} />
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] mb-1" style={{ color: GOLD }}>{f.tag}</div>
                <div className="font-semibold text-sm">{f.title}</div>
                <div className="text-xs text-[#0F3D3A]/65 mt-1.5 leading-relaxed">{f.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supply-chain / Scope 3 band */}
      <section className="bg-[#0F3D3A] text-[#F2F4F3]" data-testid="supply-chain-band">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] mb-3" style={{ color: GOLD }}>Supply chain &amp; Scope 3</div>
            <h2 className="text-3xl font-bold tracking-tight" style={serif}>AI advises. Humans decide.</h2>
            <p className="text-[#F2F4F3]/70 text-sm mt-4 leading-relaxed max-w-md">
              Scope 3 is where most of your footprint hides — and where data is messiest. Lestari's AI maps supplier
              data and drafts decarbonisation plans, but nothing enters your GHG inventory until a human accepts it.
              Supplier facts stay separate from your official ledger, connected through a governed bridge.
            </p>
            <button onClick={go} className="mt-7 inline-flex items-center gap-2 font-bold text-sm px-6 py-3 rounded-full transition-colors shadow-lg"
              style={{ background: GOLD, color: INK }} data-testid="supply-chain-cta">
              Explore the Supply Chain Control Tower <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <ul className="space-y-3" data-testid="supply-chain-points">
            {[
              ["Governed Scope 3 Data Bridge", "Supplier-origin records start as 'awaiting GHG approval' — accept, adjust or reject before anything counts."],
              ["AI Scope 3 mapper (advisory)", "AI classifies supplier and product data and flags items for review — you stay in control of the ledger."],
              ["Decarbonisation Opportunity Engine", "Rank suppliers by reduction impact and draft advisory improvement plans your team can edit and own."],
              ["PACT V3 exchange readiness", "Machine-to-machine PCF exchange built to the PACT V3 specification — ready to connect real partners."],
            ].map(([t, b]) => (
              <li key={t} className="rounded-xl bg-white/[0.06] border border-white/10 p-4">
                <div className="font-semibold text-sm flex items-center gap-2"><Network className="h-4 w-4" style={{ color: GOLD }} />{t}</div>
                <div className="text-xs text-[#F2F4F3]/65 mt-1.5 leading-relaxed">{b}</div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Who it's for */}
      <section className="border-t border-[#0F3D3A]/10 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#117A7A] mb-3">Who it&apos;s for</div>
          <h2 className="text-3xl font-bold tracking-tight max-w-2xl" style={serif}>Built for every leader with skin in the sustainability game.</h2>
          <p className="text-[#0F3D3A]/65 text-sm mt-3 max-w-2xl">From the growing SME to the SGX-listed board — and the sustainability, finance and operations leaders in between.</p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10" data-testid="leader-audiences">
            {AUDIENCES.map((a) => (
              <div key={a.title} className="rounded-xl border border-[#0F3D3A]/10 bg-[#F2F4F3] p-5" data-testid={`audience-${a.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="h-9 w-9 rounded-lg flex items-center justify-center mb-3" style={{ background: "#117A7A14", border: "1px solid #117A7A33" }}>
                  <a.icon className="h-4.5 w-4.5" style={{ color: TEAL }} />
                </div>
                <div className="font-semibold text-sm">{a.title}</div>
                <div className="text-xs text-[#0F3D3A]/65 mt-1.5 leading-relaxed">{a.body}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 rounded-xl bg-[#F2F4F3] border border-[#0F3D3A]/10 p-5 max-w-3xl border-l-4" style={{ borderLeftColor: GOLD }} data-testid="ai-climate-risk-highlight">
            <div className="flex items-start gap-3">
              <ThermometerSun className="h-5 w-5 mt-0.5 shrink-0" style={{ color: GOLD }} />
              <p className="text-sm leading-relaxed">
                <span className="font-semibold" style={{ color: TEAL }}>AI-Powered Climate Risk Assessment</span>{" "}
                <span className="text-[#0F3D3A]/70">
                  — the scenario-driven analysis that once took a consulting engagement is built in. SMEs get a
                  bank-and-tender-ready view of heat, flood and transition exposure in minutes; listed companies get IFRS
                  S2 / TCFD-aligned, assurance-ready quantification with S$ exposure the board can defend.
                </span>
              </p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-5 mt-6">
            {[{
              testid: "segment-sme", icon: Store, title: "SMEs — win the business that now demands ESG",
              intro: "Your biggest customers, tenders and banks are already asking for sustainability data — and you don't have an ESG team. Lestari gives you one, in software.",
              points: [
                "Start from utility bills and fuel cards — AI does the mapping, factors and anomaly checks",
                "Answer corporate and government tender ESG questionnaires with credible, evidenced numbers",
                "Get matched to Singapore grants and bank green loans that pay for your upgrades",
                "A voluntary sustainability report your banker and buyers can actually verify",
              ],
            }, {
              testid: "segment-listed", icon: Building2, title: "Listed companies — audit-ready at a fraction of the cost",
              intro: "Replace consultant-built spreadsheets with a governed system of record that stands up to assurance — and cuts the annual reporting bill.",
              points: [
                "IFRS S1/S2 and SGX Core Metrics coverage tracked line by line, with owners and due dates",
                "Locked base-year baselines, verification status and evidence trails on every figure",
                "Approval workflows and one-click iXBRL / ESEF / PDF exports for filing and investors",
                "Board-level control tower, quantified climate-risk register and CFO capital view — no re-keying",
              ],
            }].map((seg) => (
              <div key={seg.testid} className="rounded-xl bg-[#F2F4F3] border border-[#0F3D3A]/10 p-6 border-l-4" style={{ borderLeftColor: TEAL }} data-testid={seg.testid}>
                <div className="flex items-center gap-2.5 mb-3">
                  <seg.icon className="h-5 w-5" style={{ color: TEAL }} />
                  <span className="font-bold text-lg" style={serif}>{seg.title}</span>
                </div>
                <p className="text-sm text-[#0F3D3A]/70 leading-relaxed">{seg.intro}</p>
                <ul className="mt-4 space-y-2">
                  {seg.points.map((x) => (
                    <li key={x} className="flex items-start gap-2 text-xs text-[#0F3D3A]/70">
                      <Check className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: TEAL }} />{x}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Adaptation & climate risk */}
      <section className="bg-[#0F3D3A] text-[#F2F4F3]">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] mb-3" style={{ color: GOLD }}>Adaptation, not just mitigation</div>
            <h2 className="text-3xl font-bold tracking-tight" style={serif}>Climate risk, quantified — and answered with funded adaptation.</h2>
            <p className="text-[#F2F4F3]/70 text-sm mt-4 leading-relaxed max-w-md">
              Singapore&apos;s heat, dry spells and intense rainfall are operational risks today. Lestari assesses them
              against NGFS climate scenarios, puts a dollar figure on your exposure, and turns each risk into an
              adaptation plan with an owner, a budget — and a funding route.
            </p>
            <button onClick={go} className="mt-7 inline-flex items-center gap-2 font-bold text-sm px-6 py-3 rounded-full transition-colors shadow-lg"
              style={{ background: GOLD, color: INK }} data-testid="adaptation-signup">
              Sign up and assess your climate risk <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <ul className="space-y-3" data-testid="adaptation-points">
            {[
              ["Climate Risk Assessment", "Physical (acute & chronic) and transition risks scored by likelihood × impact, with S$ financial exposure and heatmaps for the board."],
              ["Scenario analysis", "NGFS pathways (e.g. Current Policies, Net Zero 2050) selected with rationale — feeding TCFD / IFRS S2 disclosure."],
              ["Adaptation management", "Risks link to mitigation controls and adaptation initiatives — heat-stress programmes, rainwater harvesting, drip irrigation, flood-resilient design — tracked to completion."],
              ["Avoided-loss economics", "The investment case builder values adaptation projects on avoided losses, so resilience competes fairly for capital and financing."],
            ].map(([t, b]) => (
              <li key={t} className="rounded-xl bg-white/[0.06] border border-white/10 p-4">
                <div className="font-semibold text-sm flex items-center gap-2"><ThermometerSun className="h-4 w-4" style={{ color: GOLD }} />{t}</div>
                <div className="text-xs text-[#F2F4F3]/65 mt-1.5 leading-relaxed">{b}</div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#117A7A] mb-3">How it works</div>
          <h2 className="text-3xl font-bold tracking-tight" style={serif}>Live in four steps.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {STEPS.map((s) => (
              <div key={s.n} className="relative">
                <div className="font-mono text-5xl font-bold" style={{ color: "#117A7A22" }}>{s.n}</div>
                <div className="font-semibold text-sm mt-2">{s.title}</div>
                <div className="text-xs text-[#0F3D3A]/65 mt-1.5 leading-relaxed">{s.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-t border-[#0F3D3A]/10 scroll-mt-16">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#117A7A] mb-3">Pricing</div>
          <h2 className="text-3xl font-bold tracking-tight" style={serif}>Simple, per-user pricing.</h2>
          <p className="text-[#0F3D3A]/65 text-sm mt-2 max-w-lg">The free 15-day trial runs on Lestari Lite. Growth and Enterprise are sales-led — talk to us to subscribe. Prices are per active user, per legal entity (SGD).</p>

          <div className="mt-6"><BillingCycleToggle value={cycle} onChange={setCycle} /></div>

          <div className="grid md:grid-cols-3 gap-5 mt-8">
            {packages.map((p) => {
              const featured = p.code === recCode;
              const isTrial = p.code === "lite";
              return (
                <div key={p.code} className={`rounded-xl bg-white border p-6 flex flex-col relative ${featured ? "border-[#D4A84C] ring-1 ring-[#D4A84C] shadow-lg" : "border-[#0F3D3A]/10"}`} data-testid={`pricing-${p.code}`}>
                  {featured && <span className="absolute -top-2.5 left-6 text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-full" style={{ background: GOLD, color: INK }}>MOST POPULAR</span>}
                  <div className="font-bold text-xl" style={serif}>{p.name}</div>
                  <div className="text-xs text-[#0F3D3A]/60 mt-0.5">{p.tagline}</div>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="font-mono text-4xl font-bold">{cycle === "annual" ? formatSgd(annualAmount(p.price_sgd)) : formatSgd(p.price_sgd)}</span>
                    <span className="text-xs text-[#0F3D3A]/60">{cycle === "annual" ? "/user/yr" : "/user/mo"}</span>
                  </div>
                  {cycle === "annual" && <div className="text-[11px] mt-1 font-mono" style={{ color: TEAL }}>Billed yearly · 2 months free</div>}
                  <div className="text-xs text-[#0F3D3A]/65 mt-4 border-t border-[#0F3D3A]/10 pt-4 leading-relaxed">{p.best_for}</div>
                  <ul className="mt-4 space-y-2 flex-1">
                    {p.included_features.slice(0, 7).map((f) => (
                      <li key={f} className="flex items-start gap-2 text-xs">
                        <Check className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: TEAL }} />
                        <span className={f.endsWith(":") ? "font-semibold" : "text-[#0F3D3A]/65"}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {isTrial ? (
                    <button onClick={go} data-testid={`pricing-cta-${p.code}`}
                      className={`w-full mt-6 inline-flex items-center justify-center gap-2 font-semibold text-sm px-4 py-2.5 rounded-full transition-colors ${featured ? "bg-[#0F3D3A] hover:bg-[#117A7A] text-[#F2F4F3]" : "border border-[#0F3D3A]/25 hover:bg-[#0F3D3A]/5 text-[#0F3D3A]"}`}>
                      Start free trial <ArrowRight className="h-4 w-4" />
                    </button>
                  ) : (
                    <button onClick={() => openSubscribe(p.name)} data-testid={`pricing-cta-${p.code}`}
                      className={`w-full mt-6 inline-flex items-center justify-center gap-2 font-semibold text-sm px-4 py-2.5 rounded-full transition-colors ${featured ? "bg-[#0F3D3A] hover:bg-[#117A7A] text-[#F2F4F3]" : "border border-[#0F3D3A]/25 hover:bg-[#0F3D3A]/5 text-[#0F3D3A]"}`}>
                      Subscribe <ArrowRight className="h-4 w-4" />
                    </button>
                  )}
                  <div className="text-[11px] text-[#0F3D3A]/55 mt-2 text-center font-mono">Up to {p.max_users} users</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Verified badge showcase */}
      <section className="bg-white border-t border-[#0F3D3A]/10">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#117A7A] mb-3">Build trust</div>
            <h2 className="text-3xl font-bold tracking-tight" style={serif}>Show the world you&apos;re <span style={{ color: TEAL }}>Verified by Lestari</span>.</h2>
            <p className="text-[#0F3D3A]/65 text-sm mt-3 leading-relaxed max-w-md">
              Every organisation that completes onboarding gets a public, embeddable trust badge. Drop it on your
              website, proposals and tenders to signal verified ESG governance — linked to a live verification page.
            </p>
            <ul className="mt-5 space-y-2">
              {["Verified Singapore entity", "Live ESG membership & package status", "Copy-paste iframe embed code"].map((x) => (
                <li key={x} className="flex items-center gap-2 text-sm"><BadgeCheck className="h-4 w-4" style={{ color: GOLD }} />{x}</li>
              ))}
            </ul>
          </div>
          <div className="flex justify-center">
            <VerifiedBadgePreview />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0F3D3A] text-[#F2F4F3]">
        <div className="max-w-6xl mx-auto px-6 py-16 text-center">
          <img src="/brand/logo_icon.png" alt="" className="h-14 mx-auto mb-5 brightness-110" />
          <h2 className="text-3xl lg:text-4xl font-bold tracking-tight" style={serif}>Measure it. Report it. De-risk it. Decarbonise it. Fund it.</h2>
          <p className="text-[#F2F4F3]/70 text-sm mt-3">Start with your work email and provision your secure tenant in minutes — free for 15 days, no credit card.</p>
          <button onClick={go} data-testid="cta-start-trial"
            className="mt-7 inline-flex items-center gap-2 font-bold text-sm px-7 py-3 rounded-full transition-colors shadow-lg"
            style={{ background: GOLD, color: INK }}>
            <ShieldCheck className="h-4 w-4" /> Start free trial <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>

      <footer className="bg-[#0F3D3A] border-t border-white/10 text-[#F2F4F3]">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#F2F4F3]/60">
          <div className="flex items-center gap-2.5" data-testid="footer-sustek">
            <img src="/brand/logo_icon.png" alt="" className="h-5" />
            <span><span className="font-semibold text-[#F2F4F3]/90">LESTARI AI by SusTek</span> — Sustainability · Intelligence · Impact</span>
          </div>
          <div className="flex gap-5">
            <Link to="/about" className="hover:text-[#F2F4F3]" data-testid="footer-about">About</Link>
            <Link to="/login" className="hover:text-[#F2F4F3]">Sign in</Link>
            <a href="#pricing" className="hover:text-[#F2F4F3]">Pricing</a>
            <Link to="/get-started" className="hover:text-[#F2F4F3]">Start trial</Link>
          </div>
        </div>
      </footer>
      <Footer />
    </div>
  );
}

function VerifiedBadgePreview() {
  return (
    <div className="rounded-xl bg-white border border-[#0F3D3A]/10 shadow-xl p-5 w-[300px]" data-testid="verified-badge-preview">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-lg bg-[#0F3D3A] flex items-center justify-center shrink-0 p-1.5">
          <img src="/brand/logo_icon.png" alt="" className="h-full object-contain brightness-110" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#D4A84C" }}>Verified by Lestari</div>
          <div className="font-bold text-sm leading-tight mt-0.5" style={serif}>Your Organisation Pte Ltd</div>
          <div className="text-[10px] text-[#0F3D3A]/55 font-mono mt-0.5">ESG member since 2026 · 6 material topics</div>
        </div>
      </div>
    </div>
  );
}
